import dayjs from "dayjs";
import * as React from "react";

import { type DateComparator, isValueless } from "../FilterSchema";

import styles from "./DateValueInput.module.css";

interface DateValueInputProps {
  comparator: DateComparator;
  value: string[];
  onChange: (next: string[]) => void;
}

// `<input type="datetime-local">` value shape. Minute precision — the native
// picker omits seconds without a `step`, which is plenty for range filtering.
const INPUT_FORMAT = "YYYY-MM-DDTHH:mm";

/**
 * Local-time datetime helpers. The `datetime-local` input speaks a wall-clock
 * datetime with no timezone; we interpret it as the viewer's LOCAL time
 * (matching how the tables render timestamps) and store UTC ISO 8601 on the
 * wire. `dayjs(...)` parses/formats in local time and `toISOString()` applies
 * the offset, so the round trip is stable for a given timezone.
 */
function fromIsoToInputValue(iso: string | undefined): string {
  // UTC ISO → local wall-clock "YYYY-MM-DDTHH:mm" for the input's display.
  return iso ? dayjs(iso).format(INPUT_FORMAT) : "";
}

function inputValueToIso(local: string): string {
  // Local wall-clock "YYYY-MM-DDTHH:mm" → UTC ISO. Empty input → empty string
  // so callers can omit the value cleanly.
  return local === "" ? "" : dayjs(local).toISOString();
}

/**
 * Value input for date-typed filters.
 *
 * Operator-aware:
 *   - BETWEEN renders two datetime pickers (start + end); both must be filled.
 *   - LESS_THAN ("before") / GREATER_THAN ("after") render a single datetime picker.
 *   - Relative presets (TODAY / LAST_N_DAYS / THIS_MONTH) are valueless —
 *     `isValueless` short-circuits this component to render nothing, and
 *     `filterToCondition` resolves the range from `now` at query time.
 *
 * Values are UTC ISO 8601 instants converted from the local wall-clock the
 * user picks. BETWEEN is a UI-only comparator that `filterToCondition` expands
 * into an AND of GREATER_THAN_OR_EQUAL / LESS_THAN_OR_EQUAL using the exact
 * instants; the single-value comparators pass through to the wire as-is.
 *
 * The `<input type="datetime-local">`s are UNCONTROLLED (`defaultValue`, not
 * `value`) on purpose. A native datetime input reports an empty `value` until
 * every segment is filled, so mid-typing form state is `""`. Binding `value`
 * back would let any re-render (this form validates `onChange`, and
 * `form.watch` re-renders on every keystroke) rewrite the DOM to `""`, wiping
 * the half-typed field — the classic "can't type a value" bug. Going
 * uncontrolled means React never writes the DOM after mount, so the browser
 * owns the edit buffer; `onChange` still syncs committed values to the form.
 * `defaultValue` re-seeds on mount and whenever the operator flips
 * BETWEEN↔single (different element subtree, so React remounts the input) —
 * which covers the chip-edit seeding flow.
 */
export function DateValueInput({
  comparator,
  value,
  onChange,
}: DateValueInputProps) {
  const valueless = isValueless(comparator);
  const isBetween = comparator === "BETWEEN";

  // Normalize the date arity.
  // If the user switches between BETWEEN -> BEFORE/AFTER, this truncates the value
  // so stale values aren't kept. Runs before paint.
  React.useLayoutEffect(() => {
    if (!valueless && !isBetween && value.length > 1) {
      onChange(value.slice(0, 1));
    }
  }, [valueless, isBetween, value, onChange]);

  if (valueless) {
    return null;
  }

  const handleSingleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const iso = inputValueToIso(event.target.value);
    onChange(iso === "" ? [] : [iso]);
  };

  const handleRangeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: 0 | 1,
  ) => {
    const iso = inputValueToIso(event.target.value);
    const next: string[] = [value[0] ?? "", value[1] ?? ""];
    next[index] = iso;
    onChange(next);
  };

  return (
    <div className={styles.input}>
      {isBetween ? (
        <div className={styles.range}>
          <input
            type="datetime-local"
            className={styles.field}
            aria-label="Start date and time"
            defaultValue={fromIsoToInputValue(value[0])}
            onChange={(event) => handleRangeChange(event, 0)}
          />
          <span className={styles.rangeSep} aria-hidden="true">
            ↓
          </span>
          <input
            type="datetime-local"
            className={styles.field}
            aria-label="End date and time"
            defaultValue={fromIsoToInputValue(value[1])}
            onChange={(event) => handleRangeChange(event, 1)}
          />
        </div>
      ) : (
        <input
          type="datetime-local"
          className={styles.field}
          aria-label="Date and time"
          defaultValue={fromIsoToInputValue(value[0])}
          onChange={handleSingleChange}
        />
      )}
    </div>
  );
}
